import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  KeyRound,
  Users as UsersIcon,
  Search,
  X,
  Copy,
  UserPlus,
} from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import PageHeader from "../../components/system/PageHeader";
import Card from "../../components/system/Card";
import Button from "../../components/system/Button";
import Input, { Select } from "../../components/system/Input";
import EmptyState from "../../components/system/EmptyState";
import UserStatusBadge from "../../components/system/UserStatusBadge";
import {
  OperatorTable,
  OperatorTableRow,
} from "../../components/system/OperatorTable";

/**
 * OperatorUsersPage — `/platform/companies/:cid/users`
 *
 * Tenant-wide users table. Query-string driven filters so the same
 * component powers BOTH the "Total users" and "Active users" cards.
 *
 *   ?status=all|active|inactive
 *   ?role=admin|manager|employee
 *   ?store=<storeId>
 *   ?department=<deptId>
 *   ?q=<freetext>
 *
 * Phase 1 row actions: ONLY "Mint reset link" (existing operator endpoint).
 * Activate / Deactivate / Edit deferred per user instruction.
 */
export default function OperatorUsersPage() {
  const { cid } = useParams();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();

  const [companyName, setCompanyName] = useState("");
  const [users, setUsers] = useState([]);
  const [stores, setStores] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Reset-link dialog state
  const [resetForUser, setResetForUser] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [resetSubmitting, setResetSubmitting] = useState(false);

  const status = params.get("status") || "all";
  const role = params.get("role") || "all";
  const storeId = params.get("store") || "";
  const deptId = params.get("department") || "";
  const q = params.get("q") || "";

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value && value !== "all") next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const load = async () => {
    setLoading(true);
    try {
      const [det, us, st, dp] = await Promise.all([
        api.get(`/platform/companies/${cid}`),
        api.get(`/platform/companies/${cid}/users`),
        api.get(`/platform/companies/${cid}/stores`),
        api.get(`/platform/companies/${cid}/departments`),
      ]);
      setCompanyName(det.data.company?.name || "");
      setUsers(us.data.users || []);
      setStores(st.data.stores || []);
      setDepartments(dp.data.departments || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [cid]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return users.filter((u) => {
      if (status === "active" && !u.active) return false;
      if (status === "inactive" && u.active) return false;
      if (role !== "all" && u.role !== role) return false;
      if (storeId && u.store_id !== storeId) return false;
      if (deptId && u.department_id !== deptId) return false;
      if (term) {
        const hay =
          (u.email || "") +
          " " +
          (u.name || "") +
          " " +
          (u.role || "");
        if (!hay.toLowerCase().includes(term)) return false;
      }
      return true;
    });
  }, [users, status, role, storeId, deptId, q]);

  const storeName = useMemo(() => {
    const map = new Map(stores.map((s) => [s.id, s.name]));
    return (id) => (id ? map.get(id) || "—" : "—");
  }, [stores]);
  const deptName = useMemo(() => {
    const map = new Map(departments.map((d) => [d.id, d.name]));
    return (id) => (id ? map.get(id) || "—" : "—");
  }, [departments]);

  const mintResetLink = async (user) => {
    setResetForUser(user);
    setResetResult(null);
    setResetSubmitting(true);
    try {
      const { data } = await api.post(
        `/platform/companies/${cid}/admin-password-reset`,
        { user_id: user.id },
      );
      setResetResult(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
      setResetForUser(null);
    } finally {
      setResetSubmitting(false);
    }
  };

  const copyReset = async () => {
    if (!resetResult?.reset_url) return;
    try {
      await navigator.clipboard.writeText(resetResult.reset_url);
      toast.success("Reset link copied to clipboard");
    } catch {
      toast.error("Could not access clipboard — copy manually");
    }
  };

  const isActiveOnly = status === "active";
  const title = isActiveOnly ? "Active users" : "Total users";
  const subtitle = isActiveOnly
    ? `${filtered.length} active user${filtered.length === 1 ? "" : "s"} (of ${users.length} total) across this tenant.`
    : `${filtered.length} of ${users.length} users across this tenant. Filters update instantly.`;

  const GRID = "minmax(0,2fr) 1fr 1fr 1fr 1fr 1fr auto";

  return (
    <div data-testid="operator-users-page">
      <div className="mb-4">
        <Link
          to={`/platform/companies/${cid}`}
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-muted-foreground hover:text-ink"
          data-testid="operator-users-back"
        >
          <ArrowLeft size={14} />
          Back to tenant
        </Link>
      </div>

      <PageHeader
        eyebrow={`Tenant · ${companyName || cid}`}
        title={title}
        subtitle={subtitle}
        actions={
          <Button
            tier="primary"
            onClick={() => nav(`/platform/companies/${cid}/activate-admin`)}
            data-testid="operator-users-invite-button"
          >
            <UserPlus size={14} /> Invite user
          </Button>
        }
      />

      {/* Filter bar */}
      <Card variant="raised" className="p-3 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="relative lg:col-span-2">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              value={q}
              onChange={(e) => setFilter("q", e.target.value)}
              placeholder="Search name, email, role…"
              className="pl-9"
              data-testid="operator-users-search"
            />
          </div>
          <Select
            value={status}
            onChange={(e) => setFilter("status", e.target.value)}
            data-testid="operator-users-filter-status"
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </Select>
          <Select
            value={role}
            onChange={(e) => setFilter("role", e.target.value)}
            data-testid="operator-users-filter-role"
          >
            <option value="all">All roles</option>
            <option value="admin">Admin</option>
            <option value="manager">Manager</option>
            <option value="employee">Employee</option>
          </Select>
          <Select
            value={storeId}
            onChange={(e) => setFilter("store", e.target.value)}
            data-testid="operator-users-filter-store"
          >
            <option value="">All stores</option>
            {stores.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
          <Select
            value={deptId}
            onChange={(e) => setFilter("department", e.target.value)}
            data-testid="operator-users-filter-department"
          >
            <option value="">All departments</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </Select>
          {(q || status !== "all" || role !== "all" || storeId || deptId) && (
            <Button
              tier="tertiary"
              size="sm"
              onClick={() => setParams({}, { replace: true })}
              data-testid="operator-users-clear-filters"
              className="lg:col-start-5"
            >
              Clear filters
            </Button>
          )}
        </div>
      </Card>

      <OperatorTable
        gridTemplate={GRID}
        header={
          <>
            <div>Name</div>
            <div>Email</div>
            <div>Role</div>
            <div>Store</div>
            <div>Department</div>
            <div>Status</div>
            <div className="text-right">Actions</div>
          </>
        }
        loading={loading}
        empty={!loading && filtered.length === 0}
        emptyState={
          <div className="p-10">
            <EmptyState
              icon={<UsersIcon size={32} />}
              title={
                users.length === 0
                  ? "No tenant users yet"
                  : "No users match the current filters"
              }
              description={
                users.length === 0
                  ? "Use 'Create first admin' from the tenant overview."
                  : "Try clearing one or more filters."
              }
              data-testid="operator-users-empty"
            />
          </div>
        }
        data-testid="operator-users-table"
      >
        {filtered.map((u) => (
          <OperatorTableRow
            key={u.id}
            gridTemplate={GRID}
            data-testid={`operator-user-row-${u.id}`}
          >
            <div className="min-w-0 font-display font-semibold truncate">
              {u.name || "—"}
            </div>
            <div className="font-mono text-xs truncate">{u.email}</div>
            <div className="font-mono text-xs uppercase">{u.role}</div>
            <div className="font-mono text-xs truncate">
              {storeName(u.store_id)}
            </div>
            <div className="font-mono text-xs truncate">
              {deptName(u.department_id)}
            </div>
            <div>
              <UserStatusBadge
                active={!!u.active}
                data-testid={`operator-user-status-${u.id}`}
              />
            </div>
            <div className="flex justify-end">
              <Button
                tier="secondary"
                size="sm"
                onClick={() => mintResetLink(u)}
                data-testid={`operator-user-mint-reset-${u.id}`}
              >
                <KeyRound size={12} /> Reset link
              </Button>
            </div>
          </OperatorTableRow>
        ))}
      </OperatorTable>

      {/* Reset-link dialog */}
      {resetForUser && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-ink/60 px-3"
          onClick={() => {
            setResetForUser(null);
            setResetResult(null);
          }}
          data-testid="operator-users-reset-dialog"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:max-w-lg"
          >
            <Card variant="raised" className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="font-display font-black text-xl">
                  Password reset link
                </div>
                <button
                  onClick={() => {
                    setResetForUser(null);
                    setResetResult(null);
                  }}
                  aria-label="Close"
                  className="p-2 -mt-2 -mr-2 rounded-button text-ink-2 hover:text-ink"
                  data-testid="operator-users-reset-close"
                >
                  <X size={20} />
                </button>
              </div>
              {resetSubmitting && (
                <div className="font-mono text-sm text-muted-foreground">
                  Minting one-time link…
                </div>
              )}
              {resetResult && (
                <div className="space-y-3">
                  <div>
                    <div className="data-sku mb-1">User</div>
                    <div
                      className="font-display font-semibold"
                      data-testid="operator-users-reset-email"
                    >
                      {resetResult.user?.email}
                    </div>
                  </div>
                  <div>
                    <div className="data-sku mb-1">One-time reset URL</div>
                    <textarea
                      readOnly
                      value={resetResult.reset_url}
                      className="w-full font-mono text-[11px] p-3 bg-paper-sunk rounded-button border border-border/60 break-all"
                      rows={4}
                      data-testid="operator-users-reset-url"
                      onClick={(e) => e.target.select()}
                    />
                    {resetResult.expires_at && (
                      <p className="font-mono text-[11px] text-muted-foreground mt-1">
                        Expires{" "}
                        <span className="text-ink">
                          {resetResult.expires_at}
                        </span>
                        . Single-use.
                      </p>
                    )}
                  </div>
                  <Button
                    tier="primary"
                    onClick={copyReset}
                    className="w-full"
                    data-testid="operator-users-reset-copy"
                  >
                    <Copy size={14} /> Copy link
                  </Button>
                </div>
              )}
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
